import React, { useEffect, useRef, useState } from "react";
import { CaptureWindow, recordingWindowLabel } from "./loop-recording-window";
import {
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  ChevronRight,
  CircleDot,
  Download,
  RefreshCw,
  ScanLine,
  Video,
  X,
} from "lucide-react";
import { clientHeaders } from "@/api/client-access";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import "./loop.css";
import {
  ComparisonExceptions,
  ExceptionPackage,
} from "./loop-comparison-exceptions";
import {
  ComparisonOverview,
  EventComparison,
  comparisonLabel,
  ComparisonSummary,
  LprComparison,
} from "./loop-comparison";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import {
  EVENT_FILTER_KEYS,
  eventDirection,
  eventSeconds,
  selectEvents,
} from "./loop-events";

type Package = {
  id: string;
  title: string;
  site: string;
  recorded_date: string;
  display_label?: string;
  event_count?: number;
  lpr_comparison_summary?: ComparisonSummary | null;
};
type Camera = {
  id: string;
  label: string;
  duration_seconds: number;
  source_gap_count: number;
  maximum_gap_seconds: number;
  direction_labels?: Record<string, string>;
};
type Decision = {
  verdict: string;
  reviewer: string;
  note: string;
  revision: number;
  at: string;
};
type Crossing = {
  id: string;
  camera_id: string;
  kind: string;
  direction: string;
  class_label: string;
  source_seconds: number;
  interval_start: number;
  interval_end: number;
  reason?: string;
  decision?: Decision;
  history: Decision[];
  evidence: {
    poster?: string;
    clip?: string;
    start_seconds?: number;
    end_seconds?: number;
    source_gap_count?: number;
  };
};
type Review = {
  lpr_comparison?: LprComparison | null;
  dataset: Package & {
    coverage_label: string;
    accuracy_note: string;
    scope_note: string;
    time_note: string;
    capture_window?: CaptureWindow | null;
    superseded_by_package_id?: string | null;
    supersedes_package_id?: string | null;
    counting_release?: string | null;
  };
  cameras: Camera[];
  events: Crossing[];
  summary: {
    automatic_count: number;
    adjusted_count: number;
    pending_flags: number;
    reviewed_count: number;
    total_events: number;
  };
  identity_notice: string;
};
const apiBase = import.meta.env.VITE_API_BASE_URL || "";
const clock = (seconds: number) =>
  `${Math.floor((seconds || 0) / 60)
    .toString()
    .padStart(2, "0")}:${Math.floor((seconds || 0) % 60)
    .toString()
    .padStart(2, "0")}`;

async function request(
  path: string,
  token: string | null,
  options: RequestInit = {},
) {
  const response = await fetch(apiBase + path, {
    ...options,
    headers: {
      ...options.headers,
      ...clientHeaders(),
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
    },
  });
  if (!response.ok) {
    if (response.status === 401)
      window.dispatchEvent(new Event("auth:unauthorized"));
    const body = await response.json().catch(() => ({}));
    throw new Error(
      body.detail || body.message || `Request failed (${response.status})`,
    );
  }
  return response;
}

function Evidence({
  path,
  token,
  video = false,
}: {
  path?: string;
  token: string | null;
  video?: boolean;
}) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = "";
    setUrl("");
    setError("");
    if (path)
      request(path, token, { signal: controller.signal })
        .then((r) => r.blob())
        .then((blob) => {
          if (controller.signal.aborted) return;
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        })
        .catch((e) => {
          if (!controller.signal.aborted) setError(e.message);
        });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, token]);
  if (!path)
    return (
      <div className="loop-no-evidence">
        <ScanLine />
        <span>No evidence frame supplied</span>
      </div>
    );
  if (error)
    return (
      <p role="alert" className="p-6 text-red-300">
        Evidence unavailable: {error}
      </p>
    );
  if (!url) return <div className="loop-no-evidence">Loading evidence…</div>;
  return video ? (
    <video
      key={url}
      src={url}
      controls
      playsInline
      preload="metadata"
      aria-label="Crossing context clip"
    />
  ) : (
    <img src={url} alt="Vehicle crossing evidence" />
  );
}

export function Loop() {
  const { token, user, accessEnabled } = useAuth();
  const readOnly = accessEnabled && user?.role === "viewer";
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);
  const view = location.pathname.split("/")[2] || "overview";
  const [packages, setPackages] = useState<Package[]>([]);
  const [showEmpty, setShowEmpty] = useState(false);
  const [data, setData] = useState<Review | null>(null);
  const [exceptionPackages, setExceptionPackages] = useState<
    ExceptionPackage[]
  >([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);
  const [exceptionsError, setExceptionsError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [note, setNote] = useState("");
  const [playClip, setPlayClip] = useState(false);
  const eventList = useRef<HTMLDivElement>(null);
  const visiblePackages = packages.filter(
    (item) =>
      showEmpty || item.event_count !== 0 || item.id === params.get("package"),
  );
  const packageId = params.get("package") || visiblePackages[0]?.id;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    request("/api/loop/packages", token, { signal: controller.signal })
      .then((r) => r.json())
      .then((body) => {
        if (!controller.signal.aborted) {
          setPackages(body.packages);
          if (!body.packages.length) setLoading(false);
        }
      })
      .catch((e) => {
        if (!controller.signal.aborted) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [token, refresh]);

  useEffect(() => {
    if (!packageId) {
      setData(null);
      if (packages.length) setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    setError("");
    request(`/api/loop/packages/${packageId}`, token, {
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((body) => {
        if (!controller.signal.aborted) {
          setData(body);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!controller.signal.aborted) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [token, packageId, refresh, packages.length]);

  useEffect(() => {
    const controller = new AbortController();
    setExceptionPackages([]);
    setExceptionsError("");
    if (view !== "comparison") return () => controller.abort();
    const compared = packages.filter((item) => item.lpr_comparison_summary);
    setExceptionsLoading(compared.length > 0);
    async function loadExceptions() {
      const results: ExceptionPackage[] = [];
      let failures = 0;
      // Bound concurrent requests; every response uses the active client scope.
      for (
        let i = 0;
        i < compared.length && !controller.signal.aborted;
        i += 3
      ) {
        const batch = await Promise.allSettled(
          compared.slice(i, i + 3).map(async (item) => {
            const response = await request(
              `/api/loop/packages/${item.id}`,
              token,
              {
                signal: controller.signal,
              },
            );
            const review: Review = await response.json();
            if (!review.lpr_comparison)
              throw new Error("Comparison unavailable");
            return {
              id: item.id,
              title: item.display_label || item.title,
              comparison: review.lpr_comparison,
              events: review.events,
            };
          }),
        );
        for (const result of batch) {
          if (result.status === "fulfilled") results.push(result.value);
          else failures += 1;
        }
      }
      if (controller.signal.aborted) return;
      setExceptionPackages(results);
      setExceptionsError(
        failures
          ? `${failures} recording comparisons could not be loaded. These exception counts are incomplete; use Refresh Loop results to retry.`
          : "",
      );
      setExceptionsLoading(false);
    }
    void loadExceptions();
    return () => controller.abort();
  }, [token, packages, view, refresh]);

  useEffect(() => {
    if (loading || view !== "comparison" || !location.hash) return;
    const id = location.hash.slice(1);
    if (id !== "loop-crossing-evidence" && id !== "loop-comparison-exceptions")
      return;
    document.getElementById(id)?.scrollIntoView({ block: "start" });
  }, [loading, location.hash, location.search, view, exceptionsLoading]);

  const selection = selectEvents(
    data?.events || [],
    data?.cameras || [],
    params,
    view === "review",
  );
  const { events, selected } = selection;
  const filtered = EVENT_FILTER_KEYS.some(
    (key) => key !== "sort" && params.has(key),
  );
  function requestNavigation(action: () => void) {
    if (saving) return;
    if (note.trim()) setPendingNavigation(() => action);
    else action();
  }
  function followLink(e: React.MouseEvent<HTMLAnchorElement>) {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    const url = e.currentTarget.getAttribute("href")!;
    requestNavigation(() => {
      setNote("");
      navigate(url);
    });
  }
  function updateSelection(changes: Record<string, string>, clearEvent = true) {
    const next = new URLSearchParams(params);
    if (packageId) next.set("package", packageId);
    if (clearEvent) {
      next.delete("event");
      next.delete("page");
    }
    Object.entries(changes).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key),
    );
    requestNavigation(() => {
      setNote("");
      setParams(next);
    });
  }
  function clearFilters() {
    updateSelection(
      Object.fromEntries(EVENT_FILTER_KEYS.map((key) => [key, ""])),
    );
  }
  function selectEvent(id: string) {
    updateSelection({ event: id, read: "" }, false);
  }
  const neighbors = selected?.evidence.clip
    ? (data?.events || []).filter(
        (event) =>
          event.id !== selected.id &&
          event.camera_id === selected.camera_id &&
          event.kind === "automatic" &&
          event.source_seconds >= (selected.evidence.start_seconds ?? 0) &&
          event.source_seconds <= (selected.evidence.end_seconds ?? 0),
      )
    : [];
  const camera = data?.cameras.find((item) => item.id === selected?.camera_id);
  const direction = (event: Crossing) =>
    eventDirection(event, data?.cameras || []);
  useEffect(() => {
    setNote("");
    setPlayClip(false);
  }, [selected?.id, packageId, view]);

  useEffect(() => {
    const list = eventList.current;
    const active = list?.querySelector('[aria-current="true"]');
    if (!list || !active) return;
    const row = active.getBoundingClientRect();
    const bounds = list.getBoundingClientRect();
    if (row.bottom > bounds.bottom)
      list.scrollTop += row.bottom - bounds.bottom;
    else if (row.top < bounds.top) list.scrollTop -= bounds.top - row.top;
  }, [selected?.id, selection.page, view]);

  async function save(verdict: string) {
    if (!selected || !packageId) return;
    setSaving(true);
    setError("");
    try {
      const response = await request(
        `/api/loop/packages/${packageId}/decisions`,
        token,
        {
          method: "POST",
          body: JSON.stringify({
            event_id: selected.id,
            verdict,
            note,
            expected_revision: selected.decision?.revision || 0,
          }),
        },
      );
      setData(await response.json());
      setNote("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function exportReview() {
    try {
      const response = await request(
        `/api/loop/packages/${packageId}/export`,
        token,
      );
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `loop-${packageId}.json`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div
      className={`loop-page ${view === "crossings" || view === "review" || view === "comparison" ? "loop-review-page" : ""}`}
    >
      <AlertDialog.Root
        open={!!pendingNavigation}
        onOpenChange={(open) => {
          if (!open) setPendingNavigation(null);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="loop-note-overlay" />
          <AlertDialog.Content className="loop-note-dialog">
            <AlertDialog.Title>Discard unsaved note?</AlertDialog.Title>
            <AlertDialog.Description>
              Your note has not been saved as a review decision.
            </AlertDialog.Description>
            <div className="flex justify-end gap-3 mt-5">
              <AlertDialog.Cancel asChild>
                <Button variant="outline">Keep editing</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button
                  onClick={() => {
                    const action = pendingNavigation;
                    setPendingNavigation(null);
                    setNote("");
                    action?.();
                  }}
                >
                  Discard note and continue
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
      <header className="loop-heading">
        <div>
          <h1>
            Loop<span>.</span>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={saving}
            onClick={() =>
              requestNavigation(() => {
                setNote("");
                setRefresh((x) => x + 1);
              })
            }
            aria-label="Refresh Loop results"
          >
            <RefreshCw size={16} />
          </Button>
          <Button variant="outline" disabled={!data} onClick={exportReview}>
            <Download size={16} className="mr-2" />
            Export recording review
          </Button>
        </div>
      </header>
      <div className="loop-toolbar">
        <nav aria-label="Loop views">
          {[
            ["overview", "Overview"],
            ["comparison", "Loop / LPR"],
            ["crossings", "Crossings"],
            ["review", "Review queue"],
            ["coverage", "Coverage"],
          ].map(([key, label]) => (
            <Link
              key={key}
              aria-current={view === key ? "page" : undefined}
              to={`/loop${key === "overview" ? "" : "/" + key}?${new URLSearchParams({ ...Object.fromEntries(params), ...(packageId ? { package: packageId } : {}) })}`}
              onClick={followLink}
            >
              {label}
              {key === "review" && !!data?.summary.pending_flags && (
                <span>{data.summary.pending_flags}</span>
              )}
            </Link>
          ))}
        </nav>
        {!!packages.length && (
          <label className="loop-select-label">
            Recording
            <select
              aria-label="Recording"
              value={packageId}
              disabled={saving}
              onChange={(e) => {
                const value = e.target.value;
                requestNavigation(() => {
                  setNote("");
                  setParams({ package: value });
                });
              }}
            >
              {data?.dataset.id === packageId &&
                !visiblePackages.some((item) => item.id === packageId) && (
                  <option value={packageId}>
                    {data.dataset.display_label || data.dataset.title} ·
                    Previous results
                  </option>
                )}
              {visiblePackages.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.display_label ||
                    `${item.title} · ${item.recorded_date}`}
                </option>
              ))}
            </select>
          </label>
        )}
        {packages.some((item) => item.event_count === 0) && (
          <label className="loop-empty-toggle">
            <input
              type="checkbox"
              checked={showEmpty}
              onChange={(e) => setShowEmpty(e.target.checked)}
            />
            Show recordings with no events
          </label>
        )}
      </div>
      {error && (
        <div className="loop-alert" role="alert">
          {error}{" "}
          <button onClick={() => setRefresh((x) => x + 1)}>
            Reload results
          </button>
        </div>
      )}
      {loading && (
        <p className="loop-empty" role="status">
          Loading Loop results…
        </p>
      )}
      {!loading && !error && !packages.length && (
        <div className="loop-empty">
          <ScanLine size={36} />
          <h2>Your evidence workspace is ready.</h2>
          <p>
            Completed recordings will appear here after publication. No
            recordings received yet; this does not mean zero vehicles.
          </p>
        </div>
      )}
      {!loading && !error && !!packages.length && !visiblePackages.length && (
        <p className="loop-empty">
          No recordings with events. Enable “Show recordings with no events” to
          view completed recordings with zero detections.
        </p>
      )}
      {data && (
        <>
          {data.dataset.superseded_by_package_id ? (
            <section
              className="loop-notice"
              aria-label="Updated counts available"
            >
              <span>These are the previous results for this recording. </span>
              <Link
                to={`/loop/crossings?${new URLSearchParams({ package: data.dataset.superseded_by_package_id })}`}
                onClick={followLink}
              >
                View updated counts
              </Link>
            </section>
          ) : data.dataset.supersedes_package_id ? (
            <section
              className="loop-notice"
              aria-label="Updated counting results"
            >
              <span>
                Updated counting results · Same recording, reanalyzed.{" "}
              </span>
              <Link
                to={`/loop/crossings?${new URLSearchParams({ package: data.dataset.supersedes_package_id })}`}
                onClick={followLink}
              >
                Previous results and review history
              </Link>
            </section>
          ) : null}
          {recordingWindowLabel(data.dataset.capture_window) && (
            <section
              className="loop-recording-window"
              aria-label="Recorder capture window"
            >
              <span className="loop-eyebrow">Recorder window</span>
              <strong>
                {recordingWindowLabel(data.dataset.capture_window)}
              </strong>
              <span>
                Local recording time · recorder start to saved-file completion
              </span>
            </section>
          )}
          {view === "comparison" && (
            <>
              {exceptionsLoading ? (
                <p role="status">
                  Loading exceptions across compared recordings…
                </p>
              ) : (
                <ComparisonExceptions
                  packages={exceptionPackages}
                  onLink={followLink}
                  selectedReadId={params.get("read") || undefined}
                  selectedPackageId={packageId}
                />
              )}
              {exceptionsError && <p role="alert">{exceptionsError}</p>}
            </>
          )}
          {view === "comparison" && data.lpr_comparison ? (
            <ComparisonOverview
              comparison={data.lpr_comparison}
              packages={packages}
              packageId={packageId!}
              onLink={followLink}
            />
          ) : (
            <section className="loop-metrics" aria-label="Recording summary">
              {[
                [
                  String(data.summary.adjusted_count),
                  "Provisional crossings",
                  "Automatic counts with review adjustments",
                ],
                [
                  String(data.summary.pending_flags),
                  "Needs review",
                  "Unresolved tracking gaps",
                ],
                [
                  `${data.summary.reviewed_count}/${data.summary.total_events}`,
                  "Events reviewed",
                  "Individual evidence decisions",
                ],
                [
                  String(data.cameras.length),
                  "Recordings received",
                  data.dataset.coverage_label,
                ],
              ].map(([value, label, detail]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                  <p>{detail}</p>
                </div>
              ))}
            </section>
          )}
          <div className="loop-notice">
            <CircleDot size={16} />
            <p>
              <strong>Sampled coverage · Provisional results.</strong> Counts
              are not independently validated.{" "}
              {data.lpr_comparison ? (
                <>
                  Plate reads are associated by time and need vehicle review.{" "}
                  <Link
                    to={`/loop/comparison?${new URLSearchParams({ package: packageId! })}`}
                    onClick={followLink}
                  >
                    Compare Loop and LPR
                  </Link>
                  .
                </>
              ) : (
                "No plate-reader comparison is available for this recording."
              )}{" "}
              Event times below are elapsed within each recording.
            </p>
          </div>
          {view === "overview" || view === "coverage" ? (
            <>
              <div className="loop-section-title">
                <div>
                  <p className="loop-eyebrow">
                    {data.dataset.site} / {data.dataset.recorded_date}
                  </p>
                  <h2>Coverage & camera lanes</h2>
                </div>
              </div>
              <div className="loop-camera-grid">
                {data.cameras.map((item) => {
                  const laneEvents = data.events.filter(
                    (event) => event.camera_id === item.id,
                  );
                  const count = laneEvents.filter((event) =>
                    event.kind === "automatic"
                      ? event.decision?.verdict !== "excluded"
                      : event.decision?.verdict === "confirmed",
                  ).length;
                  return (
                    <article key={item.id} className="loop-camera">
                      <div className="flex justify-between gap-4">
                        <Video size={20} className="text-primary" />
                      </div>
                      <h3>{item.label}</h3>
                      <div className="loop-camera-value">
                        {count}
                        <span> provisional crossings</span>
                      </div>
                      <dl>
                        <div>
                          <dt>Recorded duration</dt>
                          <dd>{clock(item.duration_seconds)}</dd>
                        </div>
                      </dl>
                      <Link
                        to={`/loop/crossings?${new URLSearchParams({ package: packageId!, camera: item.id })}`}
                      >
                        Review crossings <ChevronRight size={16} />
                      </Link>
                    </article>
                  );
                })}
              </div>
              <section className="loop-scope">
                <h3>What this recording set can tell us</h3>
                <p>{data.dataset.scope_note}</p>
                <p>{data.dataset.time_note}</p>
                <p>
                  Coverage shown is for received recordings only. A missing lane
                  or time window is not a zero count. Reviewing detected
                  crossings cannot establish that every vehicle was detected.
                </p>
              </section>
            </>
          ) : (
            <>
              <fieldset className="loop-filters" disabled={saving}>
                <legend>Filter events in this recording set</legend>
                <div className="loop-filter-fields">
                  {[
                    {
                      key: "camera",
                      label: "Camera / lane",
                      all: "All cameras",
                      options: data.cameras.map((c) => [c.id, c.label]),
                    },
                    {
                      key: "direction",
                      label: "Direction",
                      all: "All directions",
                      options: Array.from(new Set(data.events.map(direction)))
                        .sort()
                        .map((v) => [v, v.replace(/_/g, " ")]),
                    },
                    {
                      key: "vehicle",
                      label: "Vehicle type",
                      all: "All types",
                      options: Array.from(
                        new Set(
                          data.events.map((e) => e.class_label).filter(Boolean),
                        ),
                      )
                        .sort()
                        .map((v) => [v, v]),
                    },
                    {
                      key: "status",
                      label: "Review status",
                      all: "All statuses",
                      options:
                        view === "review"
                          ? [
                              ["unreviewed", "Unreviewed"],
                              ["unresolved", "Unresolved"],
                            ]
                          : [
                              ["unreviewed", "Unreviewed"],
                              ["confirmed", "Confirmed"],
                              ["excluded", "Excluded"],
                              ["unresolved", "Unresolved"],
                            ],
                    },
                    ...(view === "review"
                      ? []
                      : [
                          {
                            key: "kind",
                            label: "Event type",
                            all: "All events",
                            options: [
                              ["automatic", "Automatic crossing"],
                              ["tracking_gap", "Tracking gap"],
                            ],
                          },
                        ]),
                  ].map((filter) => (
                    <label key={filter.key}>
                      {filter.label}
                      <select
                        value={params.get(filter.key) || ""}
                        onChange={(e) =>
                          updateSelection({ [filter.key]: e.target.value })
                        }
                      >
                        <option value="">{filter.all}</option>
                        {params.get(filter.key) &&
                          !filter.options.some(
                            ([value]) => value === params.get(filter.key),
                          ) && (
                            <option value={params.get(filter.key)!}>
                              {params.get(filter.key)} (not in this view)
                            </option>
                          )}
                        {filter.options.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                  <label>
                    From (elapsed min)
                    <input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="0"
                      value={params.get("from") || ""}
                      onChange={(e) =>
                        updateSelection({ from: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    To (elapsed min)
                    <input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="End"
                      value={params.get("to") || ""}
                      onChange={(e) => updateSelection({ to: e.target.value })}
                    />
                  </label>
                  <label>
                    Sort by elapsed time
                    <select
                      value={
                        params.get("sort") === "latest" ? "latest" : "earliest"
                      }
                      onChange={(e) =>
                        updateSelection({ sort: e.target.value })
                      }
                    >
                      <option value="earliest">Earliest first</option>
                      <option value="latest">Latest first</option>
                    </select>
                  </label>
                </div>
                <div className="loop-filter-summary">
                  <span role="status">
                    {events.length} of {selection.total}{" "}
                    {view === "review" ? "pending flags" : "events"}
                    {filtered ? " match filters" : ""}. Summary totals and
                    export cover the entire recording set.
                  </span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    disabled={!filtered && !params.has("sort")}
                  >
                    Clear filters
                  </button>
                </div>
                {selection.error && (
                  <p role="alert" className="text-destructive">
                    {selection.error}
                  </p>
                )}
                {view === "review" && params.get("kind") && (
                  <p>
                    Event type filter: {params.get("kind")} ·{" "}
                    <button
                      type="button"
                      onClick={() => updateSelection({ kind: "" })}
                    >
                      Clear event type
                    </button>
                  </p>
                )}
              </fieldset>
              <section className="loop-review-grid">
                <aside className="loop-event-panel">
                  <div className="loop-event-heading">
                    <h2>{view === "review" ? "Pending flags" : "Events"}</h2>
                    <span>{events.length}</span>
                  </div>
                  <div
                    className="loop-events"
                    ref={eventList}
                    key={`${packageId}-${selection.page}`}
                  >
                    {selection.rows.map((event) => (
                      <button
                        key={event.id}
                        className={event.id === selected?.id ? "selected" : ""}
                        aria-current={
                          event.id === selected?.id ? "true" : undefined
                        }
                        disabled={saving}
                        onClick={() => selectEvent(event.id)}
                      >
                        <span className="loop-event-icon">
                          {direction(event) === "entry" ? (
                            <ArrowDownLeft size={18} />
                          ) : (
                            <ArrowUpRight size={18} />
                          )}
                        </span>
                        <span>
                          <strong>
                            {
                              data.cameras.find(
                                (item) => item.id === event.camera_id,
                              )?.label
                            }
                          </strong>
                          <small>
                            {event.kind === "tracking_gap"
                              ? "Tracking gap"
                              : event.class_label}{" "}
                            · {direction(event)} ·{" "}
                            {event.decision?.verdict || "Unreviewed"}
                          </small>
                          {view === "comparison" && (
                            <small className="loop-event-comparison-label">
                              {comparisonLabel(
                                data.lpr_comparison?.events.find(
                                  (item) => item.event_id === event.id,
                                ),
                              )}
                            </small>
                          )}
                        </span>
                        <time>{clock(eventSeconds(event))}</time>
                      </button>
                    ))}
                  </div>
                  {!!events.length && (
                    <nav
                      className="loop-pagination"
                      aria-label="Event list pages"
                    >
                      <button
                        disabled={saving || selection.page === 1}
                        onClick={() =>
                          updateSelection({ page: String(selection.page - 1) })
                        }
                      >
                        Previous page
                      </button>
                      <span>
                        {selection.offset + 1}–
                        {selection.offset + selection.rows.length} of{" "}
                        {events.length}
                        <br />
                        Page {selection.page} of {selection.pages}
                      </span>
                      <button
                        disabled={saving || selection.page === selection.pages}
                        onClick={() =>
                          updateSelection({ page: String(selection.page + 1) })
                        }
                      >
                        Next page
                      </button>
                    </nav>
                  )}
                  {!events.length && (
                    <p className="p-6 text-muted-foreground">
                      {filtered
                        ? "No events match these filters. Clear filters or adjust the selection."
                        : view === "review"
                          ? "No unresolved tracking flags in this selection. Automatic crossings still need accuracy review."
                          : "No events in this selection."}
                    </p>
                  )}
                </aside>
                {view === "comparison" && params.has("read") && (
                  <article className="loop-evidence-panel">
                    <h2>Plate read selected</h2>
                    <p>
                      Read details and possible passages are shown in the
                      exceptions view above when available. Select a passage to
                      inspect its video.
                    </p>
                  </article>
                )}
                {selected && !(view === "comparison" && params.has("read")) && (
                  <article
                    id="loop-crossing-evidence"
                    className="loop-evidence-panel"
                  >
                    {view === "comparison" && (
                      <Link
                        to={`${location.pathname}${location.search}#loop-comparison-exceptions`}
                        onClick={followLink}
                      >
                        ← Back to exceptions
                      </Link>
                    )}
                    <nav
                      className="loop-event-navigation"
                      aria-label="Selected event navigation"
                    >
                      <button
                        disabled={saving || selection.selectedIndex <= 0}
                        onClick={() =>
                          selectEvent(events[selection.selectedIndex - 1].id)
                        }
                      >
                        ← Previous event
                      </button>
                      <span>
                        Event {selection.selectedIndex + 1} of {events.length}
                      </span>
                      <button
                        disabled={
                          saving || selection.selectedIndex >= events.length - 1
                        }
                        onClick={() =>
                          selectEvent(events[selection.selectedIndex + 1].id)
                        }
                      >
                        Next event →
                      </button>
                    </nav>
                    <header>
                      <div>
                        <p className="loop-eyebrow">
                          {selected.kind === "tracking_gap"
                            ? "REVIEW FLAG"
                            : "CROSSING EVIDENCE"}
                        </p>
                        <h2>{camera?.label}</h2>
                      </div>
                      <span className="loop-chip">
                        {clock(
                          selected.source_seconds ?? selected.interval_start,
                        )}{" "}
                        elapsed
                      </span>
                    </header>
                    <div
                      className={
                        view === "comparison"
                          ? "loop-comparison-evidence-grid"
                          : undefined
                      }
                    >
                      <div>
                        <div className="loop-evidence">
                          <Evidence
                            key={`${selected.id}-${playClip}`}
                            path={
                              playClip
                                ? selected.evidence.clip ||
                                  selected.evidence.poster
                                : selected.evidence.poster
                            }
                            token={token}
                            video={playClip && !!selected.evidence.clip}
                          />
                        </div>
                        <div className="loop-evidence-caption">
                          <span>
                            {playClip
                              ? `Surrounding video ${clock(selected.evidence.start_seconds || 0)}–${clock(selected.evidence.end_seconds || 0)} · Selected crossing ${clock(selected.source_seconds ?? selected.interval_start)}. Other vehicles may appear before or after it.`
                              : `Selected crossing at ${clock(selected.source_seconds ?? selected.interval_start)} · original camera colors`}
                          </span>
                          {selected.evidence.clip && (
                            <button onClick={() => setPlayClip(!playClip)}>
                              {playClip
                                ? "Show selected crossing"
                                : "Watch surrounding video"}
                            </button>
                          )}
                        </div>
                      </div>
                      {view === "comparison" && (
                        <EventComparison
                          comparison={data.lpr_comparison}
                          eventId={selected.id}
                        />
                      )}
                    </div>
                    {!!neighbors.length && (
                      <div className="px-5 py-3 text-xs border-b border-border text-muted-foreground">
                        <p className="mb-2">
                          Other crossings already listed within this context
                          clip:
                        </p>
                        {neighbors.map((event) => (
                          <Link
                            key={event.id}
                            className="text-primary mr-4 underline"
                            to={`/loop/crossings?package=${packageId}&event=${event.id}`}
                            onClick={followLink}
                          >
                            {clock(event.source_seconds)} · {event.class_label}{" "}
                            · already listed
                          </Link>
                        ))}
                        <p className="mt-2">
                          Review these in Crossings before counting an
                          additional vehicle.
                        </p>
                      </div>
                    )}
                    <div className="loop-decision">
                      <div className="flex justify-between gap-3">
                        <h3>
                          {selected.kind === "tracking_gap"
                            ? "Does this flag represent an additional crossing?"
                            : "Does this event represent one distinct crossing?"}
                        </h3>
                        <span className="loop-chip">{direction(selected)}</span>
                      </div>
                      {selected.reason && (
                        <p className="text-muted-foreground">
                          {selected.reason}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Exclude a duplicate if the same vehicle is already
                        counted. A context clip can include multiple events;
                        this decision applies only to the selected event.
                      </p>
                      <label htmlFor="loop-note">
                        {readOnly
                          ? "Review note (Viewer access)"
                          : "Review note"}
                      </label>
                      <textarea
                        id="loop-note"
                        disabled={readOnly}
                        maxLength={4000}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Required when excluding or leaving unresolved"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          disabled={readOnly || saving}
                          onClick={() => save("confirmed")}
                        >
                          <Check size={16} className="mr-2" />
                          Confirm crossing
                        </Button>
                        <Button
                          variant="outline"
                          disabled={readOnly || saving || !note.trim()}
                          onClick={() => save("excluded")}
                        >
                          <X size={16} className="mr-2" />
                          Exclude
                        </Button>
                        <Button
                          variant="outline"
                          disabled={readOnly || saving || !note.trim()}
                          onClick={() => save("unresolved")}
                        >
                          Leave unresolved
                        </Button>
                      </div>
                      <p className="loop-identity">{data.identity_notice}</p>
                      {!!selected.history.length && (
                        <details>
                          <summary>
                            Decision history ({selected.history.length})
                          </summary>
                          {selected.history.map((item) => (
                            <p key={item.revision}>
                              <strong>{item.verdict}</strong> · {item.reviewer}{" "}
                              · {new Date(item.at).toLocaleString()}
                              <br />
                              {item.note}
                            </p>
                          ))}
                        </details>
                      )}
                    </div>
                  </article>
                )}
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}
